/* ==========================================================================
   ZETA 商品カード レンダラ(共有モジュール)
   ==========================================================================

   Zeta 商品検索APIのレスポンス(result.items[])+ 検索JSONビューの補完データから
   商品カードのHTML文字列を組み立てる。カードHTMLを生成する箇所はこのファイルに一本化する。

   利用元:
   - assets/custom-zeta-search-result.js  … 商品一覧/検索結果ページ
   - assets/custom-zeta-featured-collections.js … TOPのランキングセクション

   実装ポリシー(実装仕様書 §1.2):
   - DOM生成はテンプレートリテラルのみ(document.createElement は使わない)
   - Zeta/URL由来の文字列は必ず escapeHtml() を通す
   - SVGはJSにハードコードせず、呼び出し元が options.icons で渡す
     (各セクションの <template data-zeta-template="icon-*"> がLiquid由来の情報源)

   【エスケープ方針の例外】card.labels_html / card.price_html / card.cardholders_html は
   自ストアのLiquid(templates/search.zeta-cards.liquid)が生成したHTMLのため escapeHtml() を通さない。
   Zeta由来の文字列(商品名・handle等)は従来どおり必ず通すこと。

   カード仕様を変えるときは snippets/product-item.liquid と
   templates/search.zeta-cards.liquid も併せて直すこと(価格・ラベル・販促はLiquid側が正)。
   ========================================================================== */

window.ZetaProductCard = (() => {
  // HTMLへ埋め込む文字列は必ずこれを通す(属性値も同様)
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  // Shopify CDN 画像のサイズ指定
  const imageUrl = (url, width) =>
    url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '';

  const numericId = (gid) => String(gid ?? '').split('/').pop();

  // 既定値は検索結果ページの従来出力と完全に一致させてある。変更するときは
  // 一覧側の見た目が変わることを承知の上で(呼び出し元でoptions指定するのが原則)
  const DEFAULT_SIZES = '(max-width: 740px) 52vw, 25vw';

  /**
   * @param {object} item   Zeta商品検索APIの result.items[] の1件
   * @param {object|null} card  検索JSONビュー(view=zeta-cards)の補完データ。未取得なら null
   * @param {object} [options]
   *   icons          {heart, 'rating-star'} … Liquid由来のSVG断片
   *   sizes          <img sizes> の値
   *   ranking        ランキング番号(1始まり)。未指定ならバッジを出さない
   *   hiddenTitle / hiddenReview / hiddenWishlist … 各要素を出力しない
   * @returns {string} カードのHTML文字列
   */
  function render(item, card, options) {
    const opts = options || {};
    const icons = opts.icons || {};
    const sizes = opts.sizes || DEFAULT_SIZES;
    const ranking = opts.ranking;

    const title = escapeHtml(item.product_title);
    const url = `/products/${encodeURIComponent(item.product_handle)}`;

    // 画面幅に応じた画像サイズを選ばせる(§3.3.2-3)
    const image = item.image_url
      ? `<img class="product-item__primary-image" loading="lazy" alt="${title}"
           src="${escapeHtml(imageUrl(item.image_url, 600))}"
           srcset="${escapeHtml(imageUrl(item.image_url, 300))} 300w,
                   ${escapeHtml(imageUrl(item.image_url, 600))} 600w,
                   ${escapeHtml(imageUrl(item.image_url, 900))} 900w"
           sizes="${escapeHtml(sizes)}">`
      : '<div class="product-item__no-image" aria-hidden="true"></div>';

    /*
      レビュー評価バッジ(§3.3.2-2)。空のマークアップを置くだけで、集計値は
      custom-zeta-voice.js が [data-zeta-rating] を走査して埋める(zeta-voice側の改修は不要)。
      data-zeta-item-code(先頭バリアントSKU)は Zeta 検索レスポンスに無いが、
      集計キーは item_group_id(=handle)のみで足りるため付けない。

      レビュー表示可否(product.metafields.freeandopen.is_product_review_enabled)は
      Zeta検索レスポンスに無いため、補完データ(search.zeta-cards.liquid)の review_enabled で判定する。
      card 未取得(初回描画・取得失敗)のうちは星を出さない。補完データが届いた時点で
      商品リストが再描画され、表示可の商品だけ星が出る(価格の遅延取得と同じ流れ)。
    */
    const stars = (icons['rating-star'] || '').repeat(5);
    const rating = (opts.hiddenReview || card?.review_enabled !== true) ? '' : `
      <a class="product-item-meta__reviews-badge text--small" href="${url}">
        <div class="rating" data-zeta-rating data-zeta-item-group-id="${escapeHtml(item.product_handle)}">
          <div class="rating__stars" role="img" aria-label="5つ星中0つ">${stars}</div>
          <span class="rating__score">0.0</span>
          <span class="rating__caption">(0件)</span>
        </div>
      </a>`;

    const wishlist = opts.hiddenWishlist ? '' : `
              <button type="button" class="product-item-meta__wishlist" data-wishlist-open
                data-product-handle="${escapeHtml(item.product_handle)}"
                data-product-id="${escapeHtml(numericId(item.product_id))}"
                data-product-variants="${escapeHtml(card?.variant_ids || '')}"
                aria-label="お気に入りに追加">
                ${icons.heart || ''}
              </button>`;

    const titleLink = opts.hiddenTitle ? '' : `
            <a href="${url}" class="product-item-meta__title">${title}</a>`;

    // ランキングバッジは snippets/product-item.liquid:11-14 と同じ構造
    // (product-item の最初の子要素であること。CSSが :first-child 前提の配置をしている)
    const hasRanking = ranking !== null && ranking !== undefined && ranking !== '';
    const rankingClass = hasRanking ? ' product-item--ranking' : '';
    const rankingAttr = hasRanking ? ` data-ranking="${escapeHtml(ranking)}"` : '';
    const rankingBadge = hasRanking
      ? `<span class="product-item__ranking" aria-hidden="true">${escapeHtml(ranking)}</span>`
      : '';

    /*
      価格・ラベル(バッジ / 売り切れ / OFF率)・販促グループは Shopify の検索JSONビューから
      遅延取得する(§3.3.1)。判定・金額整形・文言・メタオブジェクト解決はすべて
      templates/search.zeta-cards.liquid 側で完結しており、ここでは受け取ったHTMLを差し込むだけ。
    */
    return `
      <product-item class="product-item${rankingClass}${card?.sold_out ? ' product-item--sold-out' : ''}"${rankingAttr}>${rankingBadge}
        <div class="product-item__image-wrapper">
          ${card?.labels_html ? `<div class="product-item__label-list label-list">${card.labels_html}</div>` : ''}
          <a href="${url}" class="product-item__aspect-ratio aspect-ratio aspect-ratio--short">
            ${image}
          </a>
        </div>
        <div class="product-item__info">
          <div class="product-item-meta">
            <div class="product-item-meta__head">
              ${rating}${wishlist}
            </div>${titleLink}
            <div class="product-item-meta__price-list-container">${card?.price_html || ''}</div>
          </div>

          ${card?.cardholders_html || ''}
        </div>
      </product-item>`;
  }

  /**
   * @param {Array} items    Zetaの result.items[]
   * @param {Function} getCard  (item) => 補完データ or null
   * @param {object} [options]  render() と同じ。ranking は渡さず showRanking で制御する
   *   showRanking  true のとき items の並び順(1始まり)をランキング番号にする
   */
  function renderList(items, getCard, options) {
    const opts = options || {};
    return (items || []).map((item, index) => render(item, getCard ? getCard(item) : null, {
      ...opts,
      ranking: opts.showRanking ? index + 1 : opts.ranking
    })).join('');
  }

  // 初回表示用プレースホルダ(応答が来るまでの枠取り。CLS防止)
  function renderPlaceholders(count) {
    return Array.from({ length: count || 12 }, () => `
      <product-item class="product-item zeta-product-placeholder" aria-hidden="true">
        <div class="product-item__image-wrapper">
          <div class="product-item__aspect-ratio aspect-ratio aspect-ratio--short">
            <div class="product-item__no-image"></div>
          </div>
        </div>
        <div class="product-item__info">
          <div class="zeta-placeholder-line"></div>
          <div class="zeta-placeholder-line zeta-placeholder-line--short"></div>
        </div>
      </product-item>`).join('');
  }

  /** セクション内の <template data-zeta-template="icon-*"> からSVG断片を集める */
  function collectIcons(root) {
    const icons = {};
    (root || document).querySelectorAll('template[data-zeta-template]').forEach((template) => {
      icons[template.dataset.zetaTemplate.replace('icon-', '')] = template.innerHTML.trim();
    });
    return icons;
  }

  return { escapeHtml, imageUrl, numericId, render, renderList, renderPlaceholders, collectIcons };
})();
